import { useState } from 'react';
import {
  Box,
  Button,
  VStack,
  HStack,
  Text,
} from '@chakra-ui/react';
import AddressFormFields, { AddressFormData } from '../../../components/AddressFormFields';
import { ff } from '../../../theme';

interface ShippingAddress {
  name: string;
  street: string;
  city: string;
  state: string;
  zipCode: string;
  country: string;
}

interface ShippingAddressFormProps {
  onCancel: () => void;
  onSave: (addressString: string) => void;
  shippingAddress?: ShippingAddress | null;
  isEditing?: boolean;
}

const ShippingAddressForm = ({
  onCancel,
  onSave,
  shippingAddress,
  isEditing = false,
}: ShippingAddressFormProps) => {
  const [addressData, setAddressData] = useState<AddressFormData | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  // Initialize form data when component mounts or editing existing address
  const getInitialValues = () => {
    if (shippingAddress && isEditing) {
      let secondaryAddressType = '';
      let secondaryAddressNumber = '';
      
      // Parse secondary address if present
      if (shippingAddress.name !== shippingAddress.street) {
        const secondaryPart = shippingAddress.name.replace(shippingAddress.street + ', ', '');
        const match = secondaryPart.match(/^(Apt|Rm|Unit|Building|PO Box)\s+(.+)$/);
        if (match) {
          secondaryAddressType = match[1];
          secondaryAddressNumber = match[2];
        }
      }

      return {
        address: `${shippingAddress.street}, ${shippingAddress.city}, ${shippingAddress.state}, ${shippingAddress.zipCode}`,
        streetAddress: shippingAddress.street,
        city: shippingAddress.city,
        state: shippingAddress.state,
        zip: shippingAddress.zipCode,
        country: shippingAddress.country,
        secondaryAddressType,
        secondaryAddressNumber,
        isAddressValidated: true,
      };
    }
    return {
      address: '',
      streetAddress: '',
      city: '',
      state: '',
      zip: '',
      country: '',
      secondaryAddressType: '',
      secondaryAddressNumber: '',
      isAddressValidated: false,
    };
  };

  const initialValues = getInitialValues();

  const handleAddressChange = (data: AddressFormData) => {
    setAddressData(data);
  };

  const handleSave = async () => {
    if (!addressData || !addressData.isAddressValidated) {
      return;
    }

    try {
      setIsSaving(true);

      // Build the full address string
      let fullAddress = addressData.streetAddress;
      
      if (addressData.secondaryAddressNumber && addressData.secondaryAddressType) {
        fullAddress += `, ${addressData.secondaryAddressType} ${addressData.secondaryAddressNumber}`;
      }
      
      fullAddress += `, ${addressData.city}, ${addressData.state} ${addressData.zip}, ${addressData.country}`;

      onSave(fullAddress);
      onCancel();
      
    } catch (error) {
      console.error('Error saving shipping address:', error);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Box
      border="1px solid"
      borderColor={ff.line}
      borderRadius="20px"
      p={5}
      bg="white"
    >
      <Text textStyle="display" fontSize="22px" color={ff.ink} mb={5}>
        {isEditing ? 'Edit shipping address' : 'Add shipping address'}
      </Text>

      <VStack spacing={4} align="stretch">
        <AddressFormFields
          initialAddress={initialValues.address}
          initialStreetAddress={initialValues.streetAddress}
          initialCity={initialValues.city}
          initialState={initialValues.state}
          initialZip={initialValues.zip}
          initialCountry={initialValues.country}
          initialSecondaryAddressType={initialValues.secondaryAddressType}
          initialSecondaryAddressNumber={initialValues.secondaryAddressNumber}
          initialIsAddressValidated={initialValues.isAddressValidated}
          onAddressChange={handleAddressChange}
        />

        {/* Buttons */}
        <HStack spacing={3} pt={2}>
          <Button
            flex={1}
            variant="inkOutline"
            onClick={onCancel}
            h="48px"
            isDisabled={isSaving}
          >
            Cancel
          </Button>
          <Button
            flex={1}
            variant="lime"
            onClick={handleSave}
            h="48px"
            isLoading={isSaving}
            isDisabled={!addressData?.isAddressValidated}
            loadingText="Saving..."
          >
            {isEditing ? 'Save' : 'Add'}
          </Button>
        </HStack>
      </VStack>
    </Box>
  );
};

export default ShippingAddressForm;
